import crypto from 'crypto';
/**
 * AES-256-GCM encryption/decryption utility.
 * Requires a 32-byte key (64 hex characters).
 */
const ALGORITHM = 'aes-256-gcm';
const IV_LENGTH = 12; // GCM recommended IV length
const AUTH_TAG_LENGTH = 16;
const ENCRYPTED_PREFIX = 'pxl:v1:';
/**
 * Encrypts a string using a hex-encoded 32-byte key.
 * Returns a prefixed string: pxl:v1:iv:authTag:encryptedContent (all hex).
 */
export function encrypt(text, keyHex) {
    if (!keyHex)
        throw new Error('Encryption key is required.');
    const key = Buffer.from(keyHex, 'hex');
    if (key.length !== 32) {
        throw new Error('Encryption key must be 32 bytes (64 hex characters).');
    }
    const iv = crypto.randomBytes(IV_LENGTH);
    const cipher = crypto.createCipheriv(ALGORITHM, key, iv);
    let encrypted = cipher.update(text, 'utf8', 'hex');
    encrypted += cipher.final('hex');
    const authTag = cipher.getAuthTag().toString('hex');
    return `${ENCRYPTED_PREFIX}${iv.toString('hex')}:${authTag}:${encrypted}`;
}
/**
 * Decrypts a string using a hex-encoded 32-byte key.
 * Expects format: pxl:v1:iv:authTag:encryptedContent
 */
export function decrypt(payload, keyHex) {
    if (!keyHex)
        throw new Error('Decryption key is required.');
    if (!payload.startsWith(ENCRYPTED_PREFIX)) {
        throw new Error('Payload is not in a recognized encrypted format.');
    }
    const data = payload.slice(ENCRYPTED_PREFIX.length);
    const key = Buffer.from(keyHex, 'hex');
    if (key.length !== 32) {
        throw new Error('Decryption key must be 32 bytes (64 hex characters).');
    }
    const parts = data.split(':');
    if (parts.length !== 3) {
        throw new Error('Invalid encrypted data format. Expected iv:authTag:encryptedContent');
    }
    const [ivHex, authTagHex, encryptedHex] = parts;
    const iv = Buffer.from(ivHex, 'hex');
    const authTag = Buffer.from(authTagHex, 'hex');
    const encryptedText = Buffer.from(encryptedHex, 'hex');
    const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
    decipher.setAuthTag(authTag);
    let decrypted = decipher.update(encryptedText, 'hex', 'utf8');
    decrypted += decipher.final('utf8');
    return decrypted;
}
/**
 * Checks if a string is encrypted using our format.
 * Validates that the string has the proper structure: pxl:v1:iv:authTag:encryptedContent
 */
export function isEncrypted(text) {
    if (typeof text !== 'string' || !text.startsWith(ENCRYPTED_PREFIX)) {
        return false;
    }
    // Remove prefix and check that we have the expected structure (3 colon-separated parts)
    const data = text.slice(ENCRYPTED_PREFIX.length);
    const parts = data.split(':');
    // Must have exactly 3 parts: iv, authTag, encryptedContent
    if (parts.length !== 3) {
        return false;
    }
    // Each part should be valid hex and have minimum length
    // IV: 12 bytes = 24 hex chars, AuthTag: 16 bytes = 32 hex chars, Content: at least 1 byte = 2 hex chars
    const [ivHex, authTagHex, encryptedHex] = parts;
    return (ivHex.length === 24 && /^[0-9a-f]*$/i.test(ivHex) &&
        authTagHex.length === 32 && /^[0-9a-f]*$/i.test(authTagHex) &&
        encryptedHex.length > 0 && /^[0-9a-f]*$/i.test(encryptedHex));
}
